// Uses the Transit Time app's public calendar. This is a warehouse schedule,
// not a per-item processing promise, carrier quote, or guaranteed delivery date.
const CONFIG_PATH = '/apps/subscriber-list/transit-time/config';
const CACHE_MS = 5 * 60 * 1000;
const FAILURE_CACHE_MS = 30 * 1000;
const MINUTE_MS = 60 * 1000;

function dateKey(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return '';
  const parsed = new Date(`${value}T12:00:00Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value ? value : '';
}

function addDays(value, amount) {
  const date = new Date(`${value}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + amount);
  return date.toISOString().slice(0, 10);
}

function observed(year, month, day) {
  const date = new Date(Date.UTC(year, month - 1, day, 12));
  if (date.getUTCDay() === 6) date.setUTCDate(date.getUTCDate() - 1);
  else if (date.getUTCDay() === 0) date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

function nthWeekday(year, month, weekday, occurrence) {
  const date = new Date(Date.UTC(year, month - 1, 1, 12));
  date.setUTCDate(1 + (weekday - date.getUTCDay() + 7) % 7 + (occurrence - 1) * 7);
  return date.toISOString().slice(0, 10);
}

// Identical dates to transitTimeCommonHolidaysForYear in the app. The flag is
// respected; these are not closures imposed independently by the storefront.
function commonHolidays(year) {
  const lastMayMonday = new Date(Date.UTC(year, 5, 0, 12));
  lastMayMonday.setUTCDate(lastMayMonday.getUTCDate() - (lastMayMonday.getUTCDay() - 1 + 7) % 7);
  return [
    observed(year, 1, 1), nthWeekday(year, 1, 1, 3), nthWeekday(year, 2, 1, 3),
    lastMayMonday.toISOString().slice(0, 10), observed(year, 6, 19), observed(year, 7, 4),
    nthWeekday(year, 9, 1, 1), nthWeekday(year, 10, 1, 2), observed(year, 11, 11),
    nthWeekday(year, 11, 4, 4), observed(year, 12, 25),
  ];
}

function normalizedCalendar(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw) || typeof raw.timezone !== 'string'
    || !Array.isArray(raw.shipping_days) || !raw.shipping_days.length
    || raw.shipping_days.some(day => !Number.isInteger(day) || day < 0 || day > 6)
    || typeof raw.shipping_cutoff_hhmm !== 'string' || !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(raw.shipping_cutoff_hhmm)
    || typeof raw.common_holidays_enabled !== 'boolean' || !Array.isArray(raw.custom_closed_dates)
    || !raw.holiday_calendar || typeof raw.holiday_calendar !== 'object' || Array.isArray(raw.holiday_calendar)
    || !Array.isArray(raw.holiday_calendar.universal_shipping_closed_dates)) return null;
  try { new Intl.DateTimeFormat('en-US', { timeZone: raw.timezone }).format(); } catch { return null; }
  return raw;
}

export function nextScheduledDispatch(rawCalendar, now = new Date()) {
  const calendar = normalizedCalendar(rawCalendar);
  const instant = now instanceof Date ? now : new Date(now);
  if (!calendar || !Number.isFinite(instant.getTime())) return null;
  let parts;
  try {
    parts = new Intl.DateTimeFormat('en-US', {
      timeZone: calendar.timezone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(instant);
  } catch { return null; }
  const part = name => parts.find(row => row.type === name)?.value;
  const localDate = dateKey(`${part('year')}-${part('month')}-${part('day')}`);
  if (!localDate) return null;
  const localMinute = Number(part('hour')) * 60 + Number(part('minute'));
  const [hour, minute] = calendar.shipping_cutoff_hhmm.split(':').map(Number);
  const cutoffMinute = hour * 60 + minute;
  const closed = new Set([
    ...calendar.custom_closed_dates, ...calendar.holiday_calendar.universal_shipping_closed_dates,
  ].map(dateKey).filter(Boolean));
  const year = Number(part('year'));
  if (calendar.common_holidays_enabled) {
    for (let item = year - 1; item <= year + 2; item++) for (const key of commonHolidays(item)) closed.add(key);
  }
  // The app uses a same-shipping-day cutoff and a 28-day search. Exact cutoff
  // minute remains admitted, including its seconds; do not move it a day early.
  for (let offset = 0; offset <= 28; offset++) {
    const candidate = addDays(localDate, offset);
    const weekday = new Date(`${candidate}T12:00:00Z`).getUTCDay();
    if (!calendar.shipping_days.includes(weekday) || closed.has(candidate) || offset === 0 && localMinute > cutoffMinute) continue;
    return { date: candidate, timezone: calendar.timezone, weekday, cutoffHHMM: calendar.shipping_cutoff_hhmm, waitCalendarDays: offset };
  }
  return null;
}

export function dispatchLabel(dispatch) {
  if (!dateKey(dispatch?.date)) return '';
  const date = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' })
    .format(new Date(`${dispatch.date}T12:00:00Z`));
  return `Estimated dispatch: ${date}`;
}

export function installWholesaleTransit({ document: doc, window: win, fetch: fetcher, clock = () => Date.now() }) {
  let calendar = null, expiresAt = 0, pending = null, interval = null, stopped = false;
  const nodes = () => [...doc.querySelectorAll('[data-order-dispatch]')];
  const hasOrder = () => {
    const order = doc.querySelector('tth-wholesale-order');
    return typeof order?.orderCount === 'function' && order.orderCount() > 0;
  };
  function paint() {
    const text = hasOrder() ? dispatchLabel(nextScheduledDispatch(calendar, new Date(clock()))) : '';
    for (const node of nodes()) { node.textContent = text; node.hidden = !text; }
  }
  function stopTimer() { if (interval !== null) { win.clearInterval(interval); interval = null; } }
  async function loadCalendar() {
    if (pending) return pending;
    if (clock() < expiresAt) return;
    pending = (async () => {
      const controller = new AbortController();
      const timeout = win.setTimeout(() => controller.abort(), 5000);
      try {
        // Fixed same-origin route only. Never fetch ZIP quotes, queue research,
        // write cart attributes, or follow an arbitrary URL from the payload.
        const response = await fetcher(CONFIG_PATH, { method: 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
        const payload = response.ok ? await response.json() : null;
        calendar = payload?.ok === true ? normalizedCalendar(payload.shipping_calendar) : null;
        expiresAt = clock() + (calendar ? CACHE_MS : FAILURE_CACHE_MS);
      } catch { calendar = null; expiresAt = clock() + FAILURE_CACHE_MS; }
      finally { win.clearTimeout(timeout); }
    })();
    try { await pending; } finally { pending = null; }
  }
  async function refresh() {
    if (stopped) return;
    if (doc.hidden || !nodes().length || !hasOrder()) { stopTimer(); if (!doc.hidden) paint(); return; }
    if (interval === null) interval = win.setInterval(() => { void refresh(); }, MINUTE_MS);
    await loadCalendar();
    if (!stopped) paint();
  }
  const onChange = () => { void refresh(); };
  doc.addEventListener('th:wholesale-order-change', onChange);
  doc.addEventListener('visibilitychange', onChange);
  doc.addEventListener('shopify:section:load', onChange);
  void refresh();
  return Object.freeze({ refresh, destroy() {
    stopped = true; stopTimer();
    doc.removeEventListener('th:wholesale-order-change', onChange);
    doc.removeEventListener('visibilitychange', onChange);
    doc.removeEventListener('shopify:section:load', onChange);
  } });
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const key = Symbol.for('tth.wholesale.transit.v1');
  if (!window[key]) window[key] = installWholesaleTransit({ document, window, fetch: window.fetch.bind(window) });
}
